import { useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import AnimeCard from "@/components/feature/AnimeCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { ANIME_GENRES, ANIME_REGIONS, ANIME_YEARS } from "@/mocks/types";
import { animeList } from "@/mocks/anime";

const PAGE_SIZE = 8;
const SORTS = [
  { key: "recommend", label: "默认推荐", icon: "ri-thumb-up-line" },
  { key: "updated", label: "最近更新", icon: "ri-time-line" },
  { key: "rating", label: "评分最高", icon: "ri-star-line" },
];

interface FilterGroupProps {
  label: string;
  options: { key: string; label: string }[];
  value: string;
  onSelect: (key: string) => void;
}

function FilterGroup({ label, options, value, onSelect }: FilterGroupProps) {
  return (
    <div>
      <p className="text-[11px] tracking-[0.16em] uppercase text-foreground-400 font-label mb-2">{label}</p>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt.key}
            type="button"
            onClick={() => onSelect(opt.key)}
            className={`px-3 h-9 rounded-full text-xs md:text-sm whitespace-nowrap transition-colors duration-200 cursor-pointer ${
              value === opt.key
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-700 border border-background-200 hover:border-primary-300 hover:text-primary-600"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function AnimeList() {
  const [params, setParams] = useSearchParams();
  const genre = params.get("genre") ?? "all";
  const region = params.get("region") ?? "all";
  const year = params.get("year") ?? "all";
  const sort = params.get("sort") ?? "recommend";
  const page = Math.max(1, Number(params.get("page") ?? 1));
  const [filterOpen, setFilterOpen] = useState(false);

  const update = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([key, value]) => {
      if (!value || value === "all" || value === "recommend" || value === "1") next.delete(key);
      else next.set(key, value);
    });
    setParams(next, { replace: true });
  };

  const filtered = useMemo(() => {
    let list = animeList.filter((a) => {
      if (genre !== "all" && !a.genres.includes(genre)) return false;
      if (region !== "all" && a.region !== region) return false;
      if (year !== "all" && String(a.year) !== year) return false;
      return true;
    });
    list = [...list].sort((a, b) => {
      if (sort === "updated") return a.updatedAt < b.updatedAt ? 1 : -1;
      if (sort === "rating") return b.rating - a.rating;
      return b.heat - a.heat;
    });
    return list;
  }, [genre, region, year, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const hasFilter = genre !== "all" || region !== "all" || year !== "all" || sort !== "recommend";
  const reset = () => setParams(new URLSearchParams(), { replace: true });

  const Filters = (
    <div className="flex flex-col gap-5">
      <FilterGroup
        label="动画类型"
        value={genre}
        options={[{ key: "all", label: "全部" }, ...ANIME_GENRES.map((g) => ({ key: g, label: g }))]}
        onSelect={(key) => update({ genre: key, page: "1" })}
      />
      <FilterGroup
        label="地区"
        value={region}
        options={[{ key: "all", label: "全部" }, ...ANIME_REGIONS.map((r) => ({ key: r, label: r }))]}
        onSelect={(key) => update({ region: key, page: "1" })}
      />
      <FilterGroup
        label="年份"
        value={year}
        options={[{ key: "all", label: "全部" }, ...ANIME_YEARS.map((y) => ({ key: String(y), label: `${y} 年` }))]}
        onSelect={(key) => update({ year: key, page: "1" })}
      />
    </div>
  );

  return (
    <SiteLayout
      title="动漫作品分类 - 91动漫"
      description="91动漫作品库收录国内外热门动漫作品，支持按动画类型、地区、年份筛选与默认推荐、更新时间、评分排序，快速找到想看的动画。"
      keywords="动漫作品,动画分类,热门动漫,日本动漫,国产动画,91动漫"
    >
      <section className="w-full bg-foreground-950">
        <div className="w-full px-4 md:px-8 pt-8 md:pt-12 pb-8 md:pb-10">
          <div className="text-background-300">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "动漫作品" }]} />
          </div>
          <h1 className="mt-4 font-heading font-bold text-background-50 text-2xl md:text-4xl tracking-tight">
            动漫作品分类
          </h1>
          <p className="mt-3 text-sm md:text-base text-background-300 leading-relaxed max-w-2xl">
            收录日本、中国、美国等地区的热门动画作品。按动画类型、地区、年份多维筛选，配合推荐、更新时间与评分排序，找到下一部想追的番。
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <span className="px-2.5 py-1 rounded-md bg-primary-500/15 text-primary-300 text-[11px] font-label">
              当前筛选：{genre === "all" ? "全部类型" : genre} / {region === "all" ? "全部地区" : region} /{" "}
              {year === "all" ? "全部年份" : `${year} 年`}
            </span>
            <span className="px-2.5 py-1 rounded-md bg-background-50/10 text-background-200 text-[11px] font-label">
              共 {filtered.length} 部作品
            </span>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-8 md:py-12 bg-background-50">
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8 lg:gap-10">
          <aside className="hidden lg:block">
            <div className="sticky top-24 rounded-lg border border-background-200 bg-background-100 p-5">
              {Filters}
              <div className="mt-6 pt-5 border-t border-background-200">
                <p className="text-[11px] tracking-[0.16em] uppercase text-foreground-400 font-label mb-2">排序</p>
                <div className="flex flex-col gap-2">
                  {SORTS.map((s) => (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => update({ sort: s.key, page: "1" })}
                      className={`flex items-center gap-2 h-9 px-3 rounded-md text-xs md:text-sm whitespace-nowrap transition-colors duration-200 cursor-pointer ${
                        sort === s.key
                          ? "bg-foreground-950 text-background-50"
                          : "bg-background-50 text-foreground-700 border border-background-200 hover:border-primary-300 hover:text-primary-600"
                      }`}
                    >
                      <i className={`${s.icon} text-sm`} />
                      {s.label}
                    </button>
                  ))}
                </div>
                {hasFilter && (
                  <button
                    type="button"
                    onClick={reset}
                    className="mt-4 w-full h-9 rounded-md text-xs text-foreground-600 hover:text-primary-600 border border-dashed border-background-300 transition-colors duration-200 cursor-pointer"
                  >
                    重置全部筛选
                  </button>
                )}
              </div>
            </div>
          </aside>

          <div className="min-w-0">
            <div className="lg:hidden mb-4 flex gap-2">
              <button
                type="button"
                onClick={() => setFilterOpen(true)}
                className="flex-1 h-11 rounded-md border border-background-300 bg-background-50 flex items-center justify-between px-4 text-sm text-foreground-800 cursor-pointer"
              >
                <span className="flex items-center gap-2">
                  <i className="ri-filter-3-line text-base text-primary-600" />
                  筛选条件
                </span>
                <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
                  <i className="ri-arrow-down-s-line" />
                </span>
              </button>
              <select
                value={sort}
                onChange={(e) => update({ sort: e.target.value, page: "1" })}
                className="h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 outline-none cursor-pointer"
                aria-label="排序方式"
              >
                {SORTS.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>

            {pageItems.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5" data-product-shop>
                {pageItems.map((anime, idx) => (
                  <AnimeCard key={anime.id} anime={anime} index={idx} />
                ))}
              </div>
            ) : (
              <EmptyState
                icon="ri-movie-2-line"
                title="暂无符合条件的作品"
                desc="当前筛选组合下没有作品，试试放宽年份或类型条件。"
                onReset={reset}
                actionLabel="返回首页"
                actionTo="/"
              />
            )}

            {pageItems.length > 0 && (
              <Pagination
                page={currentPage}
                totalPages={totalPages}
                onChange={(p) => {
                  update({ page: String(p) });
                  window.scrollTo({ top: 280, behavior: "smooth" });
                }}
              />
            )}

            <p className="mt-6 text-[11px] text-foreground-400 text-center leading-relaxed">
              列表内容为演示数据，本站不提供在线播放与下载，仅作作品信息展示。
            </p>

            <div className="mt-8 p-5 rounded-lg bg-background-100 border border-background-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h2 className="font-heading font-semibold text-foreground-950 text-sm md:text-base">没找到想看的？</h2>
                <p className="mt-1 text-xs text-foreground-600">用关键词搜索作品或相关资讯。</p>
              </div>
              <Link
                to="/search"
                className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors duration-200 cursor-pointer"
              >
                <i className="ri-search-line" />
                去搜索
              </Link>
            </div>
          </div>
        </div>
      </section>

      {filterOpen && (
        <div className="fixed inset-0 z-[70] lg:hidden">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setFilterOpen(false)}
            className="absolute inset-0 bg-foreground-950/60 cursor-pointer"
          />
          <div className="absolute bottom-0 left-0 right-0 bg-background-50 rounded-t-lg p-5 pb-8 animate-sheet-up max-h-[82vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-foreground-950 text-base">筛选条件</h2>
              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                aria-label="关闭"
                className="w-9 h-9 rounded-md flex items-center justify-center text-foreground-600 hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            {Filters}
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => {
                  reset();
                  setFilterOpen(false);
                }}
                className="flex-1 h-11 rounded-md border border-background-300 text-foreground-800 text-sm font-medium cursor-pointer"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                className="flex-1 h-11 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer"
              >
                查看 {filtered.length} 部作品
              </button>
            </div>
          </div>
        </div>
      )}
    </SiteLayout>
  );
}